/**
 * Analysis Scope Hierarchy — canonical enum + per-tenant terminology.
 *
 * Internal model is fixed: aircraft / system / equipment. These canonical
 * values drive the data model, integration URNs, and cross-tool contracts.
 *
 * Display labels are per-tenant customizable. An OEM saying "Vehicle" instead
 * of "Aircraft" sets { aircraft: 'Vehicle Level' } in their tenant terminology
 * override; the canonical value remains 'aircraft'.
 *
 * See: .kiro/specs/phase1-tier1-ux-fixes/requirements.md Requirement 2
 * See: SafetyNow_Aircraft_Level_Analysis_Inputs_v3.md (L1)
 * See: SafetyNow_System_Safety_Analysis_Inputs_v2.md (L2)
 *
 * @module shared/analysisScope
 */

/** Canonical analysis scope. Never renamed — used in URNs and integrations. */
export type AnalysisScope = 'aircraft' | 'system' | 'equipment';

/** All valid scope values, exported for iteration / validation. */
export const ANALYSIS_SCOPES: readonly AnalysisScope[] = ['aircraft', 'system', 'equipment'] as const;

/** Per-tenant display-label overrides. Partial — any unset keys fall back to defaults. */
export interface ScopeTerminology {
  aircraft?: string;
  system?: string;
  equipment?: string;
}

const DEFAULT_LABELS: Record<AnalysisScope, string> = {
  aircraft: 'Aircraft Level',
  system: 'System Level',
  equipment: 'Equipment Level',
};

/**
 * Return the user-facing label for a given scope, applying per-tenant override
 * if present. Returns empty string for undefined scope (graceful) to avoid
 * "L1/undefined" leaking into the UI.
 */
export function getScopeLabel(
  scope: AnalysisScope | undefined | null,
  terminology?: ScopeTerminology,
): string {
  if (!scope) return '';
  return terminology?.[scope] ?? DEFAULT_LABELS[scope];
}

/**
 * Short-form label for tight UI contexts (Project Context Bar, breadcrumbs).
 * Strips the trailing "Level" if present in the resolved label.
 */
export function getScopeShortLabel(
  scope: AnalysisScope | undefined | null,
  terminology?: ScopeTerminology,
): string {
  const full = getScopeLabel(scope, terminology);
  if (!full) return '';
  return full.replace(/\s+Level$/i, '').trim();
}

/**
 * Canonical URN for cross-tool integrations (AssureFlow, ConnectedICD, aero-agi).
 * This value MUST NOT change — breaks integration contracts if it does.
 */
export function getScopeUrn(scope: AnalysisScope): string {
  return `ee-aero.scope.${scope}`;
}

/** Type guard: runtime validation that a string is a valid AnalysisScope. */
export function isAnalysisScope(value: unknown): value is AnalysisScope {
  return typeof value === 'string' && (ANALYSIS_SCOPES as readonly string[]).includes(value);
}
